import { ResultDisplayCharacterCard } from '@/components/results/ResultDisplayCharacterCard';
import { isSlideComponentCharacterCards, Question } from '@/types/slide';
import { isCorrectnessDeterminable } from '@/utils/score';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatDuration } from '../../format';
import { ResultContainer, ResultContent, ResultTitle } from '../ResultCard';
import { ResultCardUnsupported } from './ResultCardUnsupported';

export const ResultCardCharacterCard: React.FC<{ question: Question }> = ({ question }) => {
    const { t } = useTranslation();
    const answer = question.answers[0];
    const scored = !isCorrectnessDeterminable(question) || answer?.correct;
    const caption = question.answers.length === 0 ? t('result.message.noResult') : t('result.message.cardCaption');
    const timeSpent = formatDuration(question.answers.reduce((total, answer) => total + (new Date(answer.endTime).getTime() - new Date(answer.startTime).getTime()), 0) / 1000);
    if (!isSlideComponentCharacterCards(question)) {
        return <ResultCardUnsupported question={question} />;
    }
    return (
        <ResultContainer>
            <Stack spacing={1}>
                <Box sx={{ display: 'flex' }}>
                    <ResultTitle variant='h5'>{t('slide.components.characterCards.title')}</ResultTitle>
                    <Box sx={{ flexGrow: 1 }} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography color={question.answers.length > 0 ? 'text.secondary' : 'text.warning'}>{caption}</Typography>
                    {question.answers.length > 0 && (
                        <Typography color='text.secondary'>
                            {t('result.label.timeSpent')}: {timeSpent}
                        </Typography>
                    )}
                </Box>
            </Stack>
            <ResultContent>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2, position: 'relative' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant='subtitle2' sx={{ marginBottom: 2 }}>
                            {t('result.label.question')}
                        </Typography>
                        <ResultDisplayCharacterCard question={{ ...question, answers: [] }} />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <DoubleArrowIcon sx={{ fontSize: 48 }} />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography width='100%' variant='subtitle2' sx={{ marginBottom: 2 }}>
                            {t('result.label.answer')}
                        </Typography>
                        <ResultDisplayCharacterCard question={question} />
                    </Box>
                </Box>
            </ResultContent>
            <Box
                sx={(theme) => ({
                    backgroundColor: !answer ? 'transparent' : scored ? (theme.vars || theme).palette.success.main : (theme.vars || theme).palette.error.main,
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    p: 1,
                    borderRadius: '4px',
                })}
            >
                {!answer ? (
                    <Typography variant='h5' color='warning' textAlign={'center'}>
                        {t('result.label.noResult')}
                    </Typography>
                ) : scored ? (
                    <CheckIcon style={{ color: 'white' }} />
                ) : (
                    <CloseIcon style={{ color: 'white' }} />
                )}
            </Box>
        </ResultContainer>
    );
};
